import { A, useLocation, useNavigate } from "@solidjs/router";
import { ArrowRight, CheckCircle2, Loader2, Lock, ShieldCheck, Smartphone } from "lucide-solid";
import { Show, createResource, createSignal } from "solid-js";

import { api } from "@/lib/api";

import { PlatformMark } from "@/components/brand/PlatformMark";
import { Button } from "@/components/ui/Button";
import { FieldError, Input, Label } from "@/components/ui/Input";
import { publicCatalogApi } from "@/features/catalog/api";
import type { PublicTenant } from "@/features/catalog/types";
import { settingsApi } from "@/features/settings/api";
import { HOSTING_NOTICE } from "@/features/privacy/hosting-notice";
import { currentTenantSubdomain } from "@/lib/tenant";

import { useAuth } from "./AuthProvider";

/**
 * Branding tenant résolu pré-auth pour la page de login.
 *
 * WHY (multi-tenant CLAUDE.md §4) : la page de login est publique, pas de JWT.
 * On résout le tenant depuis `<sub>.skola.app` (cf. `currentTenantSubdomain()`)
 * puis on appelle `/public/c/:subdomain/catalog` qui inclut le branding public
 * (name, logo, tagline) — mêmes données que la landing du catalogue public.
 *
 * Fail-safe (charte §6+§7) :
 *   - subdomain non résoluble → branding Scola générique, aucune erreur UI
 *   - endpoint en erreur (404, 5xx, réseau) → branding Scola générique
 *   - logs console pour observabilité (jamais avalé silencieusement)
 */
interface TenantBrand {
  name: string;
  tagline: string;
  logoUrl: string | null;
}

const SKOLA_FALLBACK: TenantBrand = {
  name: "Scola",
  tagline: "Formation continue",
  logoUrl: null,
};

async function resolveTenantBrand(): Promise<TenantBrand> {
  const sub = currentTenantSubdomain();
  if (!sub) return SKOLA_FALLBACK;
  try {
    const catalog = await publicCatalogApi.catalog(sub);
    const tenant: PublicTenant = catalog.tenant;
    return {
      name: tenant.name || SKOLA_FALLBACK.name,
      tagline: tenant.tagline ?? SKOLA_FALLBACK.tagline,
      logoUrl: settingsApi.logoPublicUrl(tenant.logo_storage_path),
    };
  } catch (err) {
    console.error("[LoginPage] tenant brand resolution failed, falling back to Scola", err);
    return SKOLA_FALLBACK;
  }
}

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Pré-remplissage de l'email via ?email= (lien d'invitation apprenant).
  const [email, setEmail] = createSignal(
    typeof location.query.email === "string" ? location.query.email : ""
  );
  const [password, setPassword] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [step, setStep] = createSignal<"credentials" | "mfa_setup" | "mfa_challenge">("credentials");
  const [mfaToken, setMfaToken] = createSignal("");
  const [mfaCode, setMfaCode] = createSignal("");
  const [otpSecret, setOtpSecret] = createSignal("");
  const [qrDataUrl, setQrDataUrl] = createSignal("");

  // Branding tenant pré-auth — `createResource` swap automatiquement quand la
  // promesse résout ; en attendant on affiche le fallback Scola pour éviter
  // tout flash visuel d'un faux état "vide".
  const [brand] = createResource(resolveTenantBrand, { initialValue: SKOLA_FALLBACK });

  const goNext = () => {
    const next = (location.query.next as string | undefined) ?? "/";
    navigate(next, { replace: true });
  };

  const onSubmit = async (event: Event) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (step() === "credentials") {
        const result = await auth.signIn(email(), password());
        if (result.status === "mfa_setup") {
          setMfaToken(result.mfa_token);
          setOtpSecret(result.secret);
          setQrDataUrl(result.qr_data_url);
          setStep("mfa_setup");
          return;
        }
        if (result.status === "mfa_required") {
          setMfaToken(result.mfa_token);
          setStep("mfa_challenge");
          return;
        }
        goNext();
        return;
      }
      const path = step() === "mfa_setup" ? "/api/v1/auth/mfa/confirm" : "/api/v1/auth/mfa/verify";
      const grant = await api<{ access_token: string; expires_in: number; user: { id: string; email: string; role: string; tenant_id: string; learner_id: string | null; platform_admin?: boolean } }>(
        path,
        { method: "POST", body: JSON.stringify({ mfa_token: mfaToken(), code: mfaCode() }) }
      );
      await auth.establishSession(grant);
      goNext();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Connexion impossible.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main class="min-h-screen w-full bg-[var(--bg-surface)] text-[var(--fg-default)] lg:grid lg:grid-cols-[1.08fr_0.92fr]">
      {/* ─── Panneau encre : marque + aperçu produit ───────────────────────── */}
      <aside class="ink-panel relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14">
        <div class="ink-grid pointer-events-none absolute inset-0" />

        <header class="relative z-10 flex items-center gap-3">
          <Show
            when={brand().logoUrl}
            fallback={
              <PlatformMark class="h-11 w-11" testId="login-hero-brand-initial" />
            }
          >
            {(url) => (
              <div class="flex h-11 w-11 items-center justify-center overflow-hidden rounded-[13px] bg-white ring-1 ring-white/20">
                <img
                  src={url()}
                  alt={`${brand().name} logo`}
                  class="max-h-9 max-w-9 object-contain"
                  data-testid="login-hero-brand-logo"
                />
              </div>
            )}
          </Show>
          <div>
            <p class="font-display text-[19px] font-semibold leading-none tracking-[-0.01em] text-[var(--ink-fg)]">
              {brand().name}
            </p>
            <p class="mt-1 text-[12px] text-[var(--ink-fg-muted)]">{brand().tagline}</p>
          </div>
        </header>

        <section class="relative z-10 my-10 grid gap-12 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] xl:items-center">
          <div class="max-w-xl">
            <p class="eyebrow !text-[#8ea6ff]">Gestion de centre de formation</p>
            <h1 class="mt-4 font-display text-[2.75rem] font-semibold leading-[1.02] tracking-[-0.025em] text-[var(--ink-fg)] xl:text-[3.4rem]">
              La rigueur suisse, appliquée à vos formations.
            </h1>
            <p class="mt-5 max-w-md text-[15.5px] leading-relaxed text-[var(--ink-fg-muted)]">
              Catalogue, sessions, émargement par QR, conventions et dossier qualité eduQua —
              dans un seul espace, tenu au propre.
            </p>
            <ul class="mt-7 grid gap-2.5 text-[13.5px] text-[var(--ink-fg-muted)]">
              <Point text="Émargement QR en salle, horodaté et infalsifiable" />
              <Point text="Conventions et attestations générées depuis les sessions" />
              <Point text="Dossier d'audit eduQua constitué au fil de l'eau" />
              <Point text="Application, base de données et documents hébergés en Suisse, chez Infomaniak" />
            </ul>
          </div>

          <ProductGlimpse />
        </section>

        <footer class="relative z-10 flex flex-wrap items-center justify-between gap-3 text-[12px] text-[var(--ink-fg-subtle)]">
          <span class="inline-flex items-center gap-2">
            <ShieldCheck size={14} stroke-width={1.75} />
            {HOSTING_NOTICE}
          </span>
          <span>Bimcheck Consulting · {new Date().getFullYear()}</span>
        </footer>
      </aside>

      {/* ─── Formulaire ────────────────────────────────────────────────────── */}
      <section class="relative flex min-h-screen flex-col bg-[var(--bg-surface)]">
        <header class="flex items-center gap-3 px-6 pt-7 lg:hidden">
          <Show
            when={brand().logoUrl}
            fallback={
              <PlatformMark class="h-10 w-10" testId="login-mobile-brand-initial" />
            }
          >
            {(url) => (
              <div class="flex h-10 w-10 items-center justify-center overflow-hidden rounded-[12px] bg-white ring-1 ring-[var(--border-default)]">
                <img
                  src={url()}
                  alt={`${brand().name} logo`}
                  class="max-h-8 max-w-8 object-contain"
                  data-testid="login-mobile-brand-logo"
                />
              </div>
            )}
          </Show>
          <div>
            <p class="font-display text-[17px] font-semibold leading-none text-[var(--fg-strong)]">
              {brand().name}
            </p>
            <p class="mt-0.5 text-[11px] text-[var(--fg-muted)]">{brand().tagline}</p>
          </div>
        </header>

        <div class="flex flex-1 items-center justify-center px-6 py-12 lg:px-14 xl:px-24">
          <div class="page-enter w-full max-w-[400px] space-y-9">
            {/* Logo tenant centré au-dessus du formulaire (marqueur principal
             * des tests). Non affiché sur le domaine Scola générique. */}
            <Show when={brand().logoUrl}>
              {(url) => (
                <div class="flex justify-center">
                  <img
                    src={url()}
                    alt={`${brand().name} logo`}
                    class="h-20 max-w-[240px] object-contain"
                    data-testid="login-centered-brand-logo"
                  />
                </div>
              )}
            </Show>

            <div class="space-y-2.5">
              <p class="eyebrow">{step() === "credentials" ? "Connexion" : "Double authentification"}</p>
              <h2 class="font-display text-[2rem] font-semibold leading-[1.05] tracking-[-0.02em] text-[var(--fg-strong)]">
                {step() === "credentials"
                  ? "Accédez à votre espace."
                  : step() === "mfa_setup"
                    ? "Protégez le compte de direction."
                    : "Confirmez votre identité."}
              </h2>
              <p class="text-[15px] text-[var(--fg-muted)]">
                {step() === "credentials"
                  ? "Identifiants de votre centre de formation."
                  : step() === "mfa_setup"
                    ? "Scannez ce QR avec une application d’authentification, puis saisissez le code à 6 chiffres."
                    : "Saisissez le code à 6 chiffres affiché dans votre application d’authentification."}
              </p>
            </div>

            <form onSubmit={onSubmit} class="space-y-5">
              <Show when={step() === "credentials"}>
                <div>
                  <Label for="email">Adresse e-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    autocomplete="email"
                    required
                    placeholder="prenom.nom@exemple.ch"
                    value={email()}
                    onInput={(e) => setEmail(e.currentTarget.value)}
                    invalid={Boolean(error())}
                    class="h-11"
                  />
                </div>

                <div>
                  <Label for="password">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    autocomplete="current-password"
                    required
                    placeholder="••••••••••"
                    value={password()}
                    onInput={(e) => setPassword(e.currentTarget.value)}
                    invalid={Boolean(error())}
                    class="h-11"
                  />
                </div>
              </Show>

              <Show when={step() === "mfa_setup"}>
                <div class="rounded-[16px] border border-[var(--border-default)] bg-[var(--bg-elevated)] p-5 text-center">
                  <Show when={qrDataUrl()}>
                    <img src={qrDataUrl()} alt="QR de configuration 2FA" class="mx-auto h-[180px] w-[180px] rounded-lg bg-white p-1" />
                  </Show>
                  <p class="mt-3 text-[12px] text-[var(--fg-muted)]">Ou saisissez cette clé manuellement</p>
                  <p class="mt-1 break-all font-mono text-[13px] font-semibold tracking-wide text-[var(--fg-strong)]">
                    {otpSecret()}
                  </p>
                </div>
              </Show>

              <Show when={step() !== "credentials"}>
                <div>
                  <Label for="mfa-code">Code à 6 chiffres</Label>
                  <Input
                    id="mfa-code"
                    inputMode="numeric"
                    autocomplete="one-time-code"
                    required
                    maxlength={6}
                    placeholder="000000"
                    value={mfaCode()}
                    onInput={(e) => setMfaCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
                    invalid={Boolean(error())}
                    class="h-11 tracking-[0.35em]"
                  />
                </div>
              </Show>

              <Show when={error()}>
                <FieldError>{error()}</FieldError>
              </Show>

              <Button type="submit" disabled={submitting()} size="lg" class="w-full">
                <Show
                  when={submitting()}
                  fallback={
                    <>
                      {step() === "credentials" ? "Se connecter" : "Valider le code"}
                      <Show when={step() === "credentials"} fallback={<Smartphone size={16} stroke-width={2} />}>
                        <ArrowRight size={16} stroke-width={2} />
                      </Show>
                    </>
                  }
                >
                  <Loader2 size={16} class="animate-spin" />
                  {step() === "credentials" ? "Connexion en cours…" : "Vérification…"}
                </Show>
              </Button>

              <p class="flex items-center gap-2 text-[12px] text-[var(--fg-subtle)]">
                <Lock size={12} stroke-width={1.75} />
                {HOSTING_NOTICE}
              </p>
            </form>

            <Show when={step() === "credentials"}>
              <div class="rounded-[16px] border border-[var(--border-default)] bg-[var(--bg-elevated)] p-4">
                <p class="text-[14px] font-semibold text-[var(--fg-strong)]">Une autre école ?</p>
                <p class="mt-1 text-[13px] leading-relaxed text-[var(--fg-muted)]">
                  Chaque centre a son nom, son adresse et son accès. Rien n’est partagé avec une autre école.
                </p>
                <A
                  href="/signup"
                  class="mt-3 inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--accent-alt)] hover:underline"
                >
                  Créer l’accès de mon école
                  <ArrowRight size={14} />
                </A>
              </div>
            </Show>

            <ul class="space-y-2.5 border-t border-[var(--border-default)] pt-6 lg:hidden">
              <Highlight text="Catalogue, sessions, émargement QR" />
              <Highlight text="Conventions et attestations" />
              <Highlight text="Dossier d'audit eduQua tenu à jour" />
            </ul>

          </div>
        </div>
      </section>
    </main>
  );
}

/**
 * Aperçu produit statique : trois surfaces réelles de l'application (fiche
 * session, émargement, facture) composées comme une capture. Aucun chiffre
 * inventé sur le centre — uniquement des exemples de données métier.
 */
function ProductGlimpse() {
  return (
    <div class="relative hidden xl:block" aria-hidden="true">
      <div class="absolute -inset-10 rounded-full bg-[radial-gradient(closest-side,rgba(47,91,255,0.35),transparent)] blur-2xl" />

      <article class="relative w-[340px] rounded-[18px] border border-white/10 bg-[var(--bg-surface)] p-5 text-[var(--fg-default)] shadow-[var(--shadow-strong)]">
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="font-mono text-[11px] text-[var(--fg-muted)]">BIM-204</p>
            <p class="mt-1 font-display text-[17px] font-semibold leading-tight text-[var(--fg-strong)]">
              Coordination BIM — Revit & Navisworks
            </p>
          </div>
          <span class="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--success-soft)] px-2.5 py-[3px] text-[11px] font-semibold text-[var(--success)]">
            <span class="h-1.5 w-1.5 rounded-full bg-[var(--success)]" />
            Ouverte
          </span>
        </div>
        <dl class="mt-4 grid grid-cols-3 gap-3 text-[12px]">
          <div>
            <dt class="text-[var(--fg-subtle)]">Dates</dt>
            <dd class="mt-0.5 font-semibold text-[var(--fg-strong)] tabular-nums">12–14 oct.</dd>
          </div>
          <div>
            <dt class="text-[var(--fg-subtle)]">Salle</dt>
            <dd class="mt-0.5 font-semibold text-[var(--fg-strong)]">A2</dd>
          </div>
          <div>
            <dt class="text-[var(--fg-subtle)]">Places</dt>
            <dd class="mt-0.5 font-semibold text-[var(--fg-strong)] tabular-nums">14 / 16</dd>
          </div>
        </dl>
        <div class="mt-4 h-1.5 overflow-hidden rounded-full bg-[var(--bg-inset)]">
          <div class="h-full w-[87.5%] rounded-full bg-[var(--accent-base)]" />
        </div>
      </article>

      <article class="relative -mt-5 ml-12 w-[300px] rounded-[16px] border border-white/10 bg-[var(--bg-surface)] p-4 text-[var(--fg-default)] shadow-[var(--shadow-strong)]">
        <div class="flex items-center gap-3">
          <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--success-soft)] text-[var(--success)]">
            <CheckCircle2 size={18} stroke-width={2} />
          </span>
          <div class="min-w-0">
            <p class="text-[13px] font-semibold text-[var(--fg-strong)]">Émargement enregistré</p>
            <p class="truncate text-[12px] text-[var(--fg-muted)]">
              Salle A2 · <span class="font-mono tabular-nums">08:58:14</span> · M. Rochat
            </p>
          </div>
        </div>
      </article>
    </div>
  );
}

function Point(props: { text: string }) {
  return (
    <li class="flex items-start gap-2.5">
      <span class="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#8ea6ff]" />
      <span>{props.text}</span>
    </li>
  );
}

function Highlight(props: { text: string }) {
  return (
    <li class="flex items-start gap-2 text-sm text-[var(--fg-default)]">
      <CheckCircle2 size={15} stroke-width={2} class="mt-0.5 shrink-0 text-[var(--accent-alt)]" />
      <span>{props.text}</span>
    </li>
  );
}
